// Who a key press on the page belongs to: the focused element, or the page's
// own handlers (the lesson's Enter, the in-app keyboard's physical keys).

/** The button or link a pointer last pressed, until Tab moves on from it. */
let pressed: Element | null = null;
let watchers = 0;

const onPointer = (e: Event) => {
	pressed = e.target instanceof Element ? e.target.closest("button, a") : null;
};
const onTab = (e: KeyboardEvent) => {
	if (e.key === "Tab") pressed = null;
};

/**
 * Starts telling a clicked button from one reached by Tab; returns the stop.
 * Not `:focus-visible`: Chrome turns a clicked button focus-visible as soon
 * as any key is pressed, which is exactly the moment the question is asked.
 */
export function watchPointer(): () => void {
	if (watchers++ === 0) {
		document.addEventListener("pointerdown", onPointer, true);
		document.addEventListener("keydown", onTab, true);
	}
	return () => {
		if (--watchers > 0) return;
		document.removeEventListener("pointerdown", onPointer, true);
		document.removeEventListener("keydown", onTab, true);
		pressed = null;
	};
}

/**
 * Whether the element the key landed on handles it itself.
 *
 * Fields own every key. A button or link owns Space and Enter, unless a
 * pointer pressed it: Chrome leaves a clicked button focused, and a learner
 * who clicked "Keyboard" and then types a sentence means the space bar for
 * the sentence, not for that button. A button reached by Tab keeps them.
 */
export function ownsKey(target: EventTarget | null, key: string): boolean {
	if (typeof Element === "undefined" || !(target instanceof Element)) return false;
	const tag = target.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
	if (target instanceof HTMLElement && target.isContentEditable) return true;
	if (key !== " " && key !== "Enter") return false;
	if (tag !== "BUTTON" && tag !== "A") return false;
	return target !== pressed;
}
